'use client';

import { motion } from 'framer-motion';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { SetupPaymentOptionCard } from '@/components/setup/experience/SetupPaymentProcessorCard';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import {
  buildSetupPaymentSummaryFromProcessorRecord,
  type SetupPaymentSummary,
} from '@/components/setup/experience/lib/setupPaymentSummary';
import {
  isSetupConnectedProcessor,
  type SetupPaymentSelection,
  type SetupTagadapayAccount,
} from '@/components/setup/experience/lib/setupPaymentUtils';
import type { Processor } from '@/lib/api/processors';

/** Stable empty fallback so a still-loading `processors` prop doesn't churn the memo. */
type ConnectedProcessor = Pick<Processor, 'id' | 'name' | 'type' | 'enabled' | 'archivedAt' | 'options'>;
const EMPTY_PROCESSORS: ConnectedProcessor[] = [];

/** A connected processor surfaced as a pickable card on the payment step. */
interface ConnectedProcessorEntry {
  processorId: string;
  processorType: string;
  summary: SetupPaymentSummary;
}

interface SetupPaymentConnectedProcessorListProps {
  /** Account processors from `useProcessorsList` (account-scoped, archived filtered server-side). */
  processors: ConnectedProcessor[] | undefined;
  tagadapayAccounts: readonly SetupTagadapayAccount[];
  /** Processor currently live on the store, to flag with the "Active" chip. */
  activeProcessorId: string | null;
  liveProcessorId: string | null;
  selection: SetupPaymentSelection;
  /** Just-configured custom processor, merged in while the account-list refetch is pending. */
  customEntry: ConnectedProcessorEntry | null;
  disabled?: boolean;
  onSelect: (processorId: string, processorType: string) => void;
}

function toEntry(processorId: string, name: string | null, type: string): ConnectedProcessorEntry {
  const summary = buildSetupPaymentSummaryFromProcessorRecord(name, type) ?? {
    processorName: type,
    processorLogo: '',
  };
  return { processorId, processorType: type, summary };
}

export function SetupPaymentConnectedProcessorList({
  processors,
  tagadapayAccounts,
  activeProcessorId,
  liveProcessorId,
  selection,
  customEntry,
  disabled = false,
  onSelect,
}: SetupPaymentConnectedProcessorListProps) {
  const { t } = useTranslation('setup');
  const xp = 'experience.payment';

  const entries = React.useMemo<ConnectedProcessorEntry[]>(() => {
    const connected = (processors ?? EMPTY_PROCESSORS)
      .filter((processor) => isSetupConnectedProcessor(processor, liveProcessorId, tagadapayAccounts))
      .map((processor) => toEntry(processor.id, processor.name, processor.type));

    // Surface the just-added/active custom processor even before the list query refetches.
    // Never resurrect a filtered-out record; TagadaPay processors need verified account options.
    const customProcessor = processors?.find((processor) => processor.id === customEntry?.processorId);
    if (
      customEntry &&
      customEntry.processorType !== 'sandbox' &&
      customEntry.processorId !== liveProcessorId &&
      (customProcessor
        ? isSetupConnectedProcessor(customProcessor, liveProcessorId, tagadapayAccounts)
        : customEntry.processorType !== 'tagadapay' && customEntry.processorType !== 'tagadapay-router') &&
      !connected.some((entry) => entry.processorId === customEntry.processorId)
    ) {
      return [customEntry, ...connected];
    }
    return connected;
  }, [processors, tagadapayAccounts, customEntry, liveProcessorId]);

  if (entries.length === 0) return null;

  return (
    <>
      <motion.li variants={SETUP_PATH_MOTION.option} role="presentation" className="mt-2 list-none">
        <span className="text-muted-foreground text-[0.6875rem] font-semibold uppercase tracking-[0.06em]">
          {t(`${xp}.connected.title`)}
        </span>
      </motion.li>

      {entries.map((entry) => (
        <SetupPaymentOptionCard
          key={entry.processorId}
          title={entry.summary.processorName}
          description={t(`${xp}.cards.custom.description`)}
          logoSrc={entry.summary.processorLogo || undefined}
          isSelected={selection.kind === 'custom' && selection.processorId === entry.processorId}
          activeChip={activeProcessorId === entry.processorId ? t(`${xp}.activeChip`) : null}
          disabled={disabled}
          onSelect={() => onSelect(entry.processorId, entry.processorType)}
        />
      ))}
    </>
  );
}
